import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WanService } from '@app/features/wan/shared/wan.service';
import { CircuitService } from '@app/features/circuit/shared/circuit-service';
import { Circuit } from '@app/shared/models/circuit.model';
import { CradlepointsService } from '@app/features/cradlepoints/shared/cradlepoints.service';
import { Cradlepoint } from '@app/shared/models/cradlepoint/cradlepoint.model';
import { FortigatesService } from '@app/features/fortigates/shared/fortigates.service';

@Component({
    selector: 'app-wan-health',
    templateUrl: './wan-health.component.html',
    styleUrls: ['./wan-health.component.css'],
    standalone: false
})
export class WanHealthComponent implements OnInit, OnDestroy {

  isLoading: boolean;
  storeNumber: string = '';
  bandwidthUtilization: any = null;
  interfaceThroughput: any = null;
  posInterfaceThroughputTest: any = null;
  circuit: Circuit = null;
  cradlepoints: Cradlepoint[] = [];
  deviceMetrics = [];
  fortigate: any = null;
  throughputTest: any = null;
  signalHealth: any = null;
  cradlepointPort2Interface: any = null;
  constructor(
    private wanService: WanService, 
    private circuitService: CircuitService,
    private cradlepointService: CradlepointsService,
    private fortigateService: FortigatesService,
    private activatedRoute: ActivatedRoute
  ) { 
    this.activatedRoute.queryParams.subscribe(params => {
      if ('storeNumber' in params) {
        this.storeNumber = params['storeNumber'];
        this.getWanHealth();
      }
    });
  }

  ngOnInit() {
    this.storeNumber = localStorage.getItem('storeNumber');
   }

  ngOnDestroy() { }

  getWanHealth() {
    localStorage.setItem('storeNumber', this.storeNumber);
    this.isLoading = true;
    this.resetView();
    Promise.all(
      [
        this.getBandwidthUtilization(),
        this.getInterfaceThroughput(),
        this.getPosInterfaceThroughoutTest(),
        this.getCircuit(),
        this.getFortigate(),
        this.getCradlepoints(),
        this.getSignalHealth(),
        this.getCradlepointPort2Interface()
      ]
    ).then(() => {
      this.isLoading = false;
    })
  }
  
  getBandwidthUtilization() {
    const promise = new Promise((resolve, reject) => {
      this.wanService.getBandwidthUtilization(this.storeNumber).then(response => {
        response.toPromise().then(response => {
          try {
            let response_data = response["message"];
            if (response_data != null) {
              this.bandwidthUtilization = response_data
              this.bandwidthUtilization.bitsIn = Math.round(this.bandwidthUtilization.bitsIn/1000000 * 100) / 100;
              this.bandwidthUtilization.bitsOut = Math.round(this.bandwidthUtilization.bitsOut/1000000 * 100) / 100;
            }
          } catch (error) {
            console.log("Bandwidth utilization timed out");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getInterfaceThroughput() {
    const promise = new Promise((resolve, reject) => {
      this.wanService.getInterfaceThroughput(this.storeNumber).then(response => {
        response.toPromise().then(response => {
          try {
            let response_data = response["message"];
            if (response_data != null) {
              this.interfaceThroughput = response_data
              for (var myInterface in this.interfaceThroughput) {
                if (this.interfaceThroughput.hasOwnProperty(myInterface)) {
                  this.interfaceThroughput[myInterface]["in"] = Math.round(this.interfaceThroughput[myInterface]["in"]/1000000 * 100) / 100;
                  this.interfaceThroughput[myInterface]["out"] = Math.round(this.interfaceThroughput[myInterface]["out"]/1000000 * 100) / 100;
                }
              }
            }
          } catch (error) {
            console.log(error);
            console.log("Interface throughput timed out");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getPosInterfaceThroughoutTest() {
    const promise = new Promise((resolve, reject) => {
      this.wanService.getPosInterfaceThroughputTest(this.storeNumber).then(response => {
        response.toPromise().then(response => {
          try {
            let response_data = response["message"];
            if (response_data != null) {
              this.posInterfaceThroughputTest = response_data
              this.posInterfaceThroughputTest.bitsIn = Math.round(this.posInterfaceThroughputTest.bitsIn/1000000 * 8 * 100) / 100;
              this.posInterfaceThroughputTest.outOctets = Math.round(this.posInterfaceThroughputTest.outOctets/1000000 * 8 * 100) / 100;
            }
          } catch (error) {
            console.log("POS interface throughput test timed out");
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getCircuit() {
    const promise = new Promise((resolve, reject) => {
      this.circuitService.getCircuitData(this.storeNumber).then(response => {
        response.toPromise().then(circuits_from_response => {
          try {
            let response_data = circuits_from_response["message"];
            if (response_data != null) {
              this.circuit = response_data;
            }
          } catch (error) {
            console.log("Circuit timed out!")
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getCradlepoints() {
    const promise = new Promise((resolve, reject) => {
      this.cradlepointService.findByStoreNumber(this.storeNumber).then(response => {
        response.toPromise().then(cradlepoints_from_response => {
          try {
            cradlepoints_from_response["message"].forEach(cradlepoint_json => {
              let cradlepoint: Cradlepoint = cradlepoint_json;
              this.cradlepoints.push(cradlepoint);
            });
          } catch (error) {
            console.log("Cradlepoints timed out!")
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getSignalHealth() {
    const promise = new Promise((resolve, reject) => {
      this.cradlepointService.getSignalHealth(this.storeNumber).then(response => {
        response.toPromise().then(response => {
          try {
            response["message"].forEach(response_json => {
              this.signalHealth = response_json;
            });
          } catch (error) {
            console.log("Signal health timed out!")
          } finally {
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getCradlepointPort2Interface() {
    const promise = new Promise((resolve, reject) => {
      this.cradlepointService.getNetworkDevices(this.storeNumber).then(response => {
        response.toPromise().then(response => {

          try {
            response["message"][0]["data"].forEach(netDevice => {
              if (netDevice["port"] == "2") {
                this.cradlepointPort2Interface = netDevice;
              }
            });
          } catch (error) {
            console.log("Cradlepoint Port2 Interface timed out!")
          } finally {
            console.log("PORT 2 CP: ")
            console.log(this.cradlepointPort2Interface)
            resolve(true);
          }
        });
      });
    });

    return promise;
  }

  getFortigate() {
    const promise = new Promise((resolve, reject) => {
      this.fortigateService.findByStoreNumber(this.storeNumber).then(response => {
        try {
          response.toPromise().then(fortigates_from_response => {
            let response_data = fortigates_from_response["message"];
            if (response_data != null) {
              this.fortigate = response_data[0];
            }
          });
        } catch (error) {
          console.log("Fortigates timed out!")
        } finally {
          resolve(true);
        }
      });
    });

    return promise
  }

  getWanDeviceSeverity(): 'green' | 'yellow' | 'red' | 'neutral' {
    let hasDevice = false;
    let hasIssue = false;
    let hasWarn = false;
    if (this.circuit != null) {
      hasDevice = true;
      if (this.circuit.loss >= 10) hasIssue = true;
    }
    if (this.fortigate != null) {
      hasDevice = true;
      if (this.fortigate.conn_status === 2) hasIssue = true;
      else if (this.fortigate.conn_status === 0) hasWarn = true;
    }
    this.cradlepoints.forEach(cradlepoint => {
      hasDevice = true;
      if (cradlepoint.state !== 'online') hasIssue = true;
    });
    if (this.cradlepointPort2Interface != null) {
      hasDevice = true;
      if (this.cradlepointPort2Interface.connection_state !== 'connected') hasIssue = true;
    }
    if (!hasDevice) return 'neutral';
    if (hasIssue) return 'red';
    if (hasWarn) return 'yellow';
    return 'green';
  }

  resetView() {
    this.bandwidthUtilization = null;
    this.interfaceThroughput = null;
    this.posInterfaceThroughputTest = null;
    this.circuit = null;
    this.fortigate = null;
    this.cradlepoints = [];
    this.deviceMetrics = [];
    this.signalHealth = null;
    this.cradlepointPort2Interface = null;
  }
}